import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, Badge, Button, Card } from 'react-bootstrap';
import DatePeriodNavigation, { PeriodChangeEvent } from './common/DatePeriodNavigation';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import { FilterBar } from './common/FilterControls';
import type { FilterDefinition } from './common/FilterControls';
import { ApiErrorBanner } from './common/ApiErrorBanner';
import { getWebFormsSubmissions } from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  WebFormSubmission,
  GetWebFormsSubmissionsParams,
  GetWebFormsSubmissions200,
  WebFormSubmissionsSummary,
} from '../api/generated/model';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

const FETCH_LIMIT = 50;

// Filter keys this table understands. The page persists these to the URL.
export const formSubmissionFilterKeys = [
  'form_id', 'status', 'delivery_status', 'search',
] as const;

// Every status value, in filter order. Exported because the page needs to
// write the full set into the URL explicitly — see the round-trip note there.
export const ALL_STATUS_VALUES = ['accepted', 'rejected', 'config_error', 'spam'];

const STATUS_OPTIONS = [
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Failed verification' },
  { value: 'config_error', label: 'Could not verify' },
  { value: 'spam', label: 'Spam (honeypot)' },
];

const DELIVERY_OPTIONS = [
  { value: 'sent', label: 'Sent' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'not_needed', label: 'Not sent' },
];

export const statusBadge = (status?: string): { variant: string; label: string } => {
  switch (status) {
    case 'accepted': return { variant: 'success', label: 'Accepted' };
    // Spam and failed verification share a colour because they are the same
    // thing to whoever is reading this list: not a lead, ignore it. They
    // differ in how certain that is — a honeypot hit is a near-certain bot,
    // a failed verification is probably one — but that nuance belongs in the
    // label, not in a colour that pulls the eye toward the rows that least
    // deserve attention.
    case 'spam': return { variant: 'secondary', label: 'Spam' };
    case 'rejected': return { variant: 'secondary', label: 'Failed verification' };
    // Red because this one IS actionable, and is ours: verification could not
    // be performed at all, so these are likely real enquiries lost to a
    // misconfiguration rather than bot traffic.
    case 'config_error': return { variant: 'danger', label: 'Could not verify' };
    default: return { variant: 'light', label: status || '' };
  }
};

export const deliveryBadge = (status?: string): { variant: string; label: string } => {
  switch (status) {
    case 'sent': return { variant: 'success', label: 'Sent' };
    case 'pending': return { variant: 'info', label: 'Pending' };
    case 'failed': return { variant: 'danger', label: 'Failed' };
    case 'not_needed': return { variant: 'light', label: 'Not sent' };
    default: return { variant: 'light', label: status || '' };
  }
};

const formatDateTime = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

// The submitter's name and email are the two things anyone scanning this
// list actually wants, but `fields` is an open map — the site can rename or
// add keys without the backend changing. So read them defensively and fall
// back to blank rather than assuming a schema.
const submitterName = (sub: WebFormSubmission): string => {
  const f = sub.fields || {};
  return [f.first_name, f.last_name].filter(Boolean).join(' ').trim();
};

export interface FormSubmissionsTableProps {
  initialDateRange?: { start_date: string; end_date: string };
  initialPeriod?: string;
  initialFilters?: Record<string, any>;
  initialSort?: { field: string; direction: 'asc' | 'desc' };

  onDateRangeChange?: (range: { start_date: string; end_date: string }, period: string) => void;
  onFiltersChange?: (filters: Record<string, any>) => void;
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (sub: WebFormSubmission) => void;

  preferencesKey: string;
  userId?: string;
  userPreferences?: any;
  refreshKey?: number;
}

const FormSubmissionsTable: React.FC<FormSubmissionsTableProps> = ({
  initialDateRange,
  initialPeriod,
  initialFilters,
  initialSort,
  onDateRangeChange,
  onFiltersChange,
  onSortChange,
  onRowClick,
  preferencesKey,
  userId,
  userPreferences,
  refreshKey,
}) => {
  const defaultDateRange = useMemo(() => {
    if (initialDateRange) return initialDateRange;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  }, [initialDateRange]);

  const [period, setPeriod] = useState<string>(initialPeriod || 'month');
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [filters, setFilters] = useState<Record<string, any>>(() => ({ ...(initialFilters || {}) }));
  const [showFilterBar, setShowFilterBar] = useState<boolean>(() =>
    Object.values(filters).some(v => v)
  );
  const [sortField, setSortField] = useState(initialSort?.field || 'created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSort?.direction || 'desc');

  useEffect(() => { onDateRangeChange?.(dateRange, period); }, [dateRange, period, onDateRangeChange]);
  useEffect(() => { onFiltersChange?.(filters); }, [filters, onFiltersChange]);
  useEffect(() => { onSortChange?.(sortField, sortDir); }, [sortField, sortDir, onSortChange]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v).length,
    [filters]
  );

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  const getRowKey = useCallback((s: WebFormSubmission) => s.id || '', []);

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      const isAll = period === 'all';
      return {
        ...(!isAll && dateRange.start_date ? { start_date: dateRange.start_date } : {}),
        ...(!isAll && dateRange.end_date ? { end_date: dateRange.end_date } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
        // Multi-selects must pass the CSV through UNCHANGED. An empty string
        // is the "Deselect all" sentinel meaning "match nothing"; a truthiness
        // test would swallow it and send no param at all, which the backend
        // reads as "no filter" -- the exact opposite of what was clicked.
        ...(filters.form_id !== undefined ? { form_id: filters.form_id } : {}),
        ...(filters.status !== undefined ? { status: filters.status } : {}),
        ...(filters.delivery_status !== undefined ? { delivery_status: filters.delivery_status } : {}),
        // search is free text, where empty genuinely means "no filter".
        ...(filters.search ? { search: filters.search } : {}),
      };
    },
    [dateRange, filters, period, sortField, sortDir]
  );

  const {
    allItems,
    totalCount,
    apiSummary: summary,
    isLoading,
    hasMore,
    handleLoadMore,
    reset,
    error: paginationError,
  } = usePaginatedQuery<WebFormSubmission, GetWebFormsSubmissions200, WebFormSubmissionsSummary>({
    queryKey: `web-form-submissions:${preferencesKey}`,
    buildQueryParams,
    queryFn: (params) => getWebFormsSubmissions(params as GetWebFormsSubmissionsParams),
    getRowKey,
  });

  useEffect(() => {
    if (refreshKey === undefined) return;
    reset();
  }, [refreshKey, reset]);

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode);
    setDateRange({
      start_date: event.dateRange.start_date.split('T')[0],
      end_date: event.dateRange.end_date.split('T')[0],
    });
  };

  const filterDefs: FilterDefinition[] = useMemo(() => [
    {
      type: 'multiselect',
      field: 'form_id',
      label: 'Form',
      icon: 'bi-ui-checks',
      options: (summary?.forms || []).map(f => ({
        value: f.form_id,
        label: f.label || f.form_id,
      })),
    },
    {
      type: 'multiselect',
      field: 'status',
      label: 'Status',
      icon: 'bi-shield-check',
      options: STATUS_OPTIONS,
    },
    {
      type: 'multiselect',
      field: 'delivery_status',
      label: 'Delivery',
      icon: 'bi-envelope',
      options: DELIVERY_OPTIONS,
    },
    {
      type: 'text',
      field: 'search',
      label: 'Search',
      icon: 'bi-search',
      placeholder: 'Name, email, message…',
    },
  ] as FilterDefinition[], [summary?.forms]);

  const columns: ColumnConfig<WebFormSubmission>[] = useMemo(() => [
    {
      key: 'created_at',
      label: 'Received',
      sortable: true,
      width: 170,
      renderCell: (s) => formatDateTime(s.created_at),
    },
    {
      key: 'label',
      label: 'Form',
      sortable: true,
      width: 200,
      renderCell: (s) => s.label || s.form_id,
    },
    {
      key: 'name',
      label: 'Name',
      width: 180,
      renderCell: (s) => submitterName(s),
    },
    {
      key: 'email',
      label: 'Email',
      width: 240,
      renderCell: (s) => (s.fields || {}).email || '',
    },
    {
      key: 'organization',
      label: 'Organization',
      width: 220,
      renderCell: (s) => {
        const f = s.fields || {};
        // The two live forms differ here by design, so try both.
        return f.library_school_or_organization || f.company || '';
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 150,
      renderCell: (s) => {
        const b = statusBadge(s.status);
        return <Badge bg={b.variant}>{b.label}</Badge>;
      },
    },
    {
      key: 'delivery_status',
      label: 'Delivery',
      sortable: true,
      width: 130,
      renderCell: (s) => {
        const b = deliveryBadge(s.delivery_status);
        return <Badge bg={b.variant}>{b.label}</Badge>;
      },
    },
    {
      key: 'message',
      label: 'Message',
      minWidth: 260,
      renderCell: (s) => (s.fields || {}).message || '',
      tooltip: (s) => (s.fields || {}).message || '',
    },
  ], []);

  const quarantined = summary?.quarantined_count || 0;
  const deliveryFailed = summary?.delivery_failed_count || 0;

  return (
    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              <span className="text-primary fw-semibold">
                <i className="bi bi-inbox me-1"></i>
                Submissions for
              </span>
              <DatePeriodNavigation
                onPeriodChange={handlePeriodChange}
                dateFormat="date-only"
                initialNavigationMode={period as any}
                initialDateRange={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
              />
              <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilterBar(!showFilterBar)}
              >
                <i className="bi bi-funnel me-1"></i>
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="bg-header-control rounded px-3 py-1">
              <span className="text-primary">
                {totalCount !== undefined ? `${totalCount} submissions` : 'No submissions found'}
              </span>
            </div>
          </div>
        </div>
        {showFilterBar && (
          <FilterBar
            filters={filters}
            filterDefs={filterDefs}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        )}
      </Card.Header>

      {/* A Turnstile misconfiguration flags real enquiries as spam, so the
          count has to be visible on the page and not only in Slack. */}
      {quarantined > 0 && (
        <Alert variant="warning" className="m-0 rounded-0 border-0 py-2">
          <i className="bi bi-shield-exclamation me-2"></i>
          {quarantined} submission{quarantined === 1 ? '' : 's'} treated as spam and not delivered.
          Filter by status to review, and mark anything genuine as not spam.
        </Alert>
      )}
      {deliveryFailed > 0 && (
        <Alert variant="warning" className="m-0 rounded-0 border-0 py-2">
          <i className="bi bi-envelope-exclamation me-2"></i>
          {deliveryFailed} submission{deliveryFailed === 1 ? '' : 's'} persisted but not delivered.
          Open one to resend.
        </Alert>
      )}

      <ApiErrorBanner error={paginationError} />
      <Card.Body className="flex-grow-1 overflow-hidden p-0 d-flex flex-column" style={{ minHeight: 0 }}>
        <DataTable
          data={allItems}
          columns={columns}
          getRowKey={getRowKey}
          sortConfig={{ field: sortField, direction: sortDir }}
          onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
          onRowClick={onRowClick}
          pagination={{ hasMore, limit: FETCH_LIMIT }}
          onLoadMore={handleLoadMore}
          isLoading={isLoading}
          preferencesKey={preferencesKey}
          userId={userId}
          userPreferences={userPreferences}
          maxHeight="100%"
          enableStickyHeader={true}
          enableColumnReorder={true}
          enableColumnResize={true}
          enableColumnVisibility={true}
          emptyState={<Alert variant="secondary" className="m-3">No submissions found.</Alert>}
        />
      </Card.Body>
    </Card>
  );
};

export default FormSubmissionsTable;
